import { useCallback, useMemo } from 'react';

import type { EditingMode, UUID, VariableWithValue } from '@aline/common';
import { Queries, variableVariantToFieldVariant } from '@aline/common';
import type { ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import type { Core } from '@pdftron/webviewer';
import camelCase from 'camelcase';
import { equals, uniq, uniqWith } from 'ramda';
import { usePrevious } from 'react-use';
import type { Range } from 'slate';
import type { ExistingFieldRecipient, PDFFieldLocation } from 'src/components';
import { pdfTronCore, usePDFFieldAPI } from 'src/components';
import {
  getCheckboxOptionIdentities,
  toCheckboxChoiceRefIds,
} from 'src/components/variables/document-variables/checkbox-option-ids';
import {
  useEditorTabStateSelectorForTabId,
  useGetFieldsInJSON,
} from 'src/editor/editor-tab-state-registry';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  documentTypename,
  useDispatchNotification,
  useDocumentParams,
  useGetIsPDFFieldInDocument,
  useGetPDFFields,
  useGetPDFFieldsByNodeId,
} from 'src/helpers';
import { useAuthorizedTransforms } from 'src/hooks/authorized-transforms';
import { useGetCurrentDoc } from 'src/hooks/document';
import { useDisableSignatureField } from 'src/hooks/signature-field';
import {
  useCreateDocumentVariable,
  useDisableVariable,
  useGetVariablesForDocument,
} from 'src/hooks/variable';
import type { AlineClientEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import type { FieldInDocument, UserSubset, WithLocalFieldProps } from 'src/types';

export function useGetField(options: Parameters<typeof Apollo.useGetFieldQuery>[0]) {
  const { data, loading } = Apollo.useGetFieldQuery(options);
  return { field: data?.field ?? null, loading };
}

type DocumentVariable = NonNullable<
  Apollo.GetDocumentFieldsQueryHookResult['data']
>['document']['variables'][number];
type SignatureField = NonNullable<
  Apollo.GetDocumentFieldsQueryHookResult['data']
>['document']['signatureFields'][number];

export function useGetDocumentFields(documentId: UUID): {
  allSignatures: SignatureField[];
  allVariables: DocumentVariable[];
  document: NonNullable<Apollo.GetDocumentFieldsQueryHookResult['data']>['document'] | null;
  documentWithFields:
    | NonNullable<Apollo.GetDocumentFieldsQueryHookResult['data']>['document']
    | null;
  loading: boolean;
  signatureFieldsInChildren: SignatureField[];
  variablesInChildren: DocumentVariable[];
} {
  const { data, loading } = Apollo.useGetDocumentFieldsQuery({
    variables: { documentId },
  });
  const { fieldIdsInChildren } = useGetFieldsInJSON();
  // useMemo so downstream consumers of this hook don't fire unnecessarily (e.g., memo avoids returning a new array each time).
  const { allSignatures, allVariables, signatureFieldsInChildren, variablesInChildren } =
    useMemo(() => {
      const allVariables = data?.document.variables ?? [];
      const allSignatures = data?.document.signatureFields ?? [];
      return {
        allSignatures,
        allVariables,
        signatureFieldsInChildren: allSignatures.filter(sig => fieldIdsInChildren.has(sig.nodeId)),
        variablesInChildren: allVariables.filter(v => fieldIdsInChildren.has(v.nodeId)),
      };
    }, [data?.document.signatureFields, data?.document.variables, fieldIdsInChildren]);

  return {
    allSignatures,
    allVariables,
    document: data?.document ?? null,
    documentWithFields: data?.document ?? null,
    loading,
    signatureFieldsInChildren,
    variablesInChildren,
  };
}

export function useCreateSignatureField(): {
  createSignatureField: (
    options: Parameters<Apollo.CreateSignatureFieldMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateSignatureFieldMutation>>;
} {
  const signaturesKey: keyof Apollo.Document = 'signatureFields';
  const [createSignatureField] = Apollo.useCreateSignatureFieldMutation();

  return {
    createSignatureField: options => {
      return createSignatureField({
        ...options,
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              [signaturesKey]: (refs: readonly Reference[] = [], { readField }) => {
                const newRef = cache.writeFragment({
                  data: data?.createSignatureField,
                  fragment: gql`
                    fragment NewSignature on SignatureField {
                      __typename
                      id
                    }
                  `,
                });

                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  newRef ? [...refs, newRef] : refs
                );
              },
            },
            id: cache.identify({
              __typename: documentTypename,
              id: options?.variables?.input.props.documentId,
            }),
          });
        },
      });
    },
  };
}

interface CreateSignatureFieldForm {
  __typename: 'SignatureFieldForm';
  action: ExistingSigner | null;
  locationInfo: FieldLocation;
  nodeId: UUID;
}

interface CreateVariableFieldForm {
  __typename: 'VariableForm';
  action: ExistingFieldRecipient | null;
  isDynamic: boolean;
  locationInfo: FieldLocation;
  nodeId: UUID;
  value: string | null;
  variant: Apollo.MetadataVariant;
  integrationLink?: Pick<Apollo.IntegrationLink, 'attributePath' | 'integration'>;
  label?: string;
  options?: Array<{ optionText: string; triggerAction: Apollo.TriggerAction }>;
  selectedAnswerIds?: UUID[];
}
export type CreateFieldForm = CreateVariableFieldForm | CreateSignatureFieldForm;

interface UseCreateFieldOptions {
  documentId: UUID | null;
  documentViewer: Core.DocumentViewer | null;
  editingMode: EditingMode | null;
  editor: AlineClientEditor | null;
  tabId: UUID | null;
}

/**
 * Field creation can be triggered from inside the editor tree or from app-level
 * drag/drop. Keep the editor/viewer handles explicit so the caller chooses the
 * tab runtime being mutated.
 */
export function useCreateField({
  documentId,
  documentViewer,
  editingMode,
  editor,
  tabId,
}: UseCreateFieldOptions) {
  const { document } = useGetCurrentDoc(documentId);
  const { createVariable } = useCreateDocumentVariable();
  const { createSignatureField } = useCreateSignatureField();
  const { disableVariable } = useDisableVariable();
  const { insertFieldIntoDocument } = useInsertFieldIntoDocument({
    documentViewer,
    editingMode,
    editor,
    tabId,
  });
  const { deleteFieldFromDocument } = useDeleteFieldFromDocument(
    editor,
    documentViewer,
    tabId,
    documentId
  );
  const dispatchCreateFieldFailed = useDispatchNotification({
    message: 'Field creation failed.',
    variant: 'error',
  });
  const dispatchActionRequired = useDispatchNotification({
    message: 'Select a recipient before attempting to create a field.',
    variant: 'error',
  });
  const createField = useCallback(
    async (form: CreateFieldForm) => {
      if (!document) return {};
      if (!form.action) {
        dispatchActionRequired();
        return {};
      }

      switch (form.__typename) {
        case 'SignatureFieldForm': {
          await insertFieldIntoDocument({
            isDynamic: false,
            locationInfo: form.locationInfo,
            newField: { __typename: 'SignatureField', nodeId: form.nodeId },
          });
          const { data } = await createSignatureField({
            variables: {
              input: {
                props: {
                  actionId: form.action.id,
                  documentId: document.id,
                  nodeRefId: form.nodeId,
                },
              },
            },
          });
          if (!data) {
            await deleteFieldFromDocument(form.nodeId);
            dispatchCreateFieldFailed();
          }

          return { field: data?.createSignatureField ?? null };
        }
        case 'VariableForm': {
          if (!form.action) {
            dispatchActionRequired();
            return {};
          }

          if (form.variant !== Apollo.MetadataVariant.Checkbox) {
            await insertFieldIntoDocument({
              isDynamic: form.isDynamic,
              locationInfo: form.locationInfo,
              newField: {
                __typename: 'DocumentVariable',
                label: form.label || getFieldLabelFromVariant(form.variant),
                multiLine: false,
                nodeId: form.nodeId,
                value: form.value,
                variant: form.variant,
              },
            });
          }

          const isCheckbox = form.variant === Apollo.MetadataVariant.Checkbox;
          const isOptionsVariant =
            form.variant === Apollo.MetadataVariant.Options ||
            form.variant === Apollo.MetadataVariant.RadioOptions ||
            isCheckbox;
          const defaultAnswerOptions = [
            { optionText: '', triggerAction: Apollo.TriggerAction.Merge },
          ];
          const { data } = await createVariable(document.id, {
            variables: {
              input: {
                createAnswerOptions: isOptionsVariant
                  ? { options: form.options ?? defaultAnswerOptions }
                  : undefined,
                props: {
                  actionId: form.action.id,
                  attributePath: form.integrationLink?.attributePath,
                  documentId: document.id,
                  integration: form.integrationLink?.integration,
                  isField: true,
                  label: form.label || getFieldLabelFromVariant(form.variant),
                  minRequired: 0,
                  nodeId: form.nodeId,
                  question: '',
                  ...(isCheckbox
                    ? { selectedAnswerIds: form.selectedAnswerIds ?? [], value: null }
                    : { value: form.value }),
                  variant: form.variant,
                },
              },
            },
          });
          if (!data) {
            await deleteFieldFromDocument(form.nodeId);
            dispatchCreateFieldFailed();
            return { field: null };
          }

          if (form.variant === Apollo.MetadataVariant.Checkbox) {
            const createdVariable = data.createVariable;
            const checkboxOptions = createdVariable.answerOptions?.options;
            if (!checkboxOptions?.length) {
              await disableVariable(createdVariable.id, {
                variables: { id: createdVariable.id },
              });
              await deleteFieldFromDocument(form.nodeId);
              dispatchCreateFieldFailed();
              return { field: null };
            }
            const checkboxOptionsForInsert = checkboxOptions.map(option => ({
              choiceRefId: option.choiceRefId,
              id: option.id,
              optionText: option.optionText,
            }));
            try {
              await insertFieldIntoDocument({
                checkboxOptions: checkboxOptionsForInsert,
                isDynamic: form.isDynamic,
                locationInfo: form.locationInfo,
                newField: {
                  __typename: 'DocumentVariable',
                  label: form.label || getFieldLabelFromVariant(form.variant),
                  multiLine: false,
                  nodeId: form.nodeId,
                  value: null,
                  variant: form.variant,
                },
                selectedAnswerIds: form.selectedAnswerIds ?? [],
              });
            } catch {
              await disableVariable(createdVariable.id, {
                variables: { id: createdVariable.id },
              });
              await deleteFieldFromDocument(form.nodeId, {
                checkboxOptionIds: checkboxOptionsForInsert.map(option => option.id),
              });
              dispatchCreateFieldFailed();
              return { field: null };
            }
          }
          return { field: data?.createVariable ?? null };
        }
      }
    },
    [
      createSignatureField,
      createVariable,
      deleteFieldFromDocument,
      disableVariable,
      dispatchActionRequired,
      dispatchCreateFieldFailed,
      document,
      insertFieldIntoDocument,
    ]
  );

  return { createField };
}

interface ExistingSigner extends Pick<Apollo.WorkflowRecipient, 'id' | '__typename'> {
  assignee: UserSubset | null;
  type: Apollo.WorkflowRecipientType.Signer;
}
interface SetValueVariable extends Pick<Apollo.DocumentVariable, 'nodeId' | '__typename'> {
  action: ExistingSigner | null;
}

export function useSetFieldValue(
  editor: AlineClientEditor | null,
  documentViewer: Core.DocumentViewer | null,
  tabId: UUID | null
) {
  const authorId = useCurrentUserId();
  const { setPDFFieldValue } = usePDFFieldAPI(documentViewer, tabId);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const editingMode = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.documentUi.editingMode
  );

  const setFieldValue = useCallback(
    (args: {
      value: string;
      variable: SetValueVariable;
      versionTypename: Apollo.FileVersion['__typename'];
    }) => {
      const { value, variable, versionTypename } = args;

      switch (versionTypename) {
        case 'DocumentVersion': {
          if (!editingMode || !authorId || !editor) return { updated: false };

          return AuthorizedTransforms.updateInputField(editor, {
            assigneeId: variable.action?.assignee?.id ?? null,
            authorId,
            editingMode,
            fieldId: variable.nodeId,
            update: { text: value },
          });
        }
        case 'StaticFileVersion':
          return setPDFFieldValue(variable.nodeId, value);
      }
    },
    [AuthorizedTransforms, authorId, editingMode, editor, setPDFFieldValue]
  );

  return { setFieldValue };
}

export function getFieldLabelFromVariant(variant: Apollo.MetadataVariant): string {
  return `Field.${camelCase(variant)}`;
}

type DeleteField = Pick<Apollo.Field, 'id' | 'nodeId' | '__typename'> & {
  answerOptions?: { options: Array<{ id: string }> };
  variant?: Apollo.MetadataVariant;
};

export function useDeleteField(
  editor: AlineClientEditor | null,
  documentViewer: Core.DocumentViewer | null,
  tabId: UUID | null
): {
  deleteField: (field: DeleteField) => Promise<{ data?: unknown }>;
} {
  const { disableVariable } = useDisableVariable();
  const { disableSignatureField } = useDisableSignatureField();
  const { deleteFieldFromDocument } = useDeleteFieldFromDocument(editor, documentViewer, tabId);
  const deleteField = useCallback(
    async (field: DeleteField): Promise<{ data?: unknown }> => {
      const checkboxOptionIds =
        field.__typename === 'DocumentVariable' &&
        field.variant === Apollo.MetadataVariant.Checkbox &&
        field.answerOptions?.options?.length
          ? field.answerOptions.options.map(o => o.id)
          : undefined;
      await deleteFieldFromDocument(field.nodeId, { checkboxOptionIds });

      switch (field.__typename) {
        case 'DocumentVariable':
          return disableVariable(field.id, {
            variables: { id: field.id },
          });
        case 'SignatureField':
          return disableSignatureField(field.id, {
            variables: { id: field.id },
          });
      }
    },
    [deleteFieldFromDocument, disableSignatureField, disableVariable]
  );

  return { deleteField };
}

export function useDeleteFieldFromDocument(
  editor: AlineClientEditor | null,
  documentViewer: Core.DocumentViewer | null,
  tabId: UUID | null,
  documentIdOverride?: UUID | null
): {
  deleteFieldFromDocument: (
    nodeId: UUID,
    options?: { checkboxOptionIds?: UUID[] }
  ) => Promise<void>;
} {
  const { editingMode } = useEditingMode();
  const { deletePDFField } = usePDFFieldAPI(documentViewer, tabId);
  const { document } = useGetCurrentDoc(documentIdOverride);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const deleteFieldFromDocument = useCallback(
    async (nodeId: UUID, options?: { checkboxOptionIds?: UUID[] }): Promise<void> => {
      switch (document?.latestVersion.__typename) {
        case 'DocumentVersion': {
          if (!editor || !editingMode) return;

          return AuthorizedTransforms.deleteInputFieldInstances(editor, {
            editingMode,
            fieldId: nodeId,
          });
        }
        case 'StaticFileVersion': {
          return deletePDFField(nodeId, options?.checkboxOptionIds);
        }
        default:
          break;
      }
    },
    [AuthorizedTransforms, deletePDFField, document?.latestVersion.__typename, editingMode, editor]
  );

  return { deleteFieldFromDocument };
}

export function useGetIsFieldInDocument(tabId: UUID | null): {
  getIsFieldInDocument: (field: Pick<Apollo.Field, 'nodeId' | '__typename'>) => boolean;
} {
  const { fieldIdsInChildren } = useGetFieldsInJSON();
  const { getIsPDFFieldInDoc } = useGetIsPDFFieldInDocument(tabId);
  const getIsFieldInDocument = useCallback(
    (field: Pick<Apollo.Field, 'nodeId' | '__typename'>): boolean => {
      return fieldIdsInChildren.has(field.nodeId) || getIsPDFFieldInDoc(field.nodeId);
    },
    [fieldIdsInChildren, getIsPDFFieldInDoc]
  );

  return { getIsFieldInDocument };
}

type MaybeCompleteField =
  | Omit<VariableWithValue, 'label'>
  | Pick<Apollo.SignatureField, 'allSigned' | 'nodeId' | '__typename'>;

export function getIsFieldComplete(field: MaybeCompleteField): boolean {
  switch (field.__typename) {
    case 'DocumentVariable':
      return Queries.getIsDBFieldCompleted(field);
    case 'SignatureField':
      return field.allSigned;
  }
}

export function useGetFieldInDocument(nodeId: UUID, tabId: UUID | null): FieldInDocument | null {
  const {
    fieldsInJSON: { inChildren },
  } = useGetFieldsInJSON();
  const pdfFieldsByNodeId = useGetPDFFieldsByNodeId(tabId);
  const pdfField = pdfFieldsByNodeId.get(nodeId) ?? null;
  // PDF field is not "in document" unless it has some widgets...
  const safePDFField = pdfField?.widgets?.length ? pdfField : null;

  return inChildren[nodeId] ?? safePDFField ?? null;
}

export function useGetLocalFieldInfo(
  nodeId: UUID,
  tabId: UUID | null
): {
  fieldInDocument: FieldInDocument;
  localProps: WithLocalFieldProps<{ text: string }>;
} | null {
  const fieldInDocument = useGetFieldInDocument(nodeId, tabId);
  if (!fieldInDocument) return null;

  const isPDFField = fieldInDocument instanceof pdfTronCore.Annotations.Forms.Field;
  return {
    fieldInDocument,
    localProps: {
      multiLine: isPDFField
        ? Boolean(fieldInDocument.flags?.get(pdfTronCore.Annotations.WidgetFlags.MULTILINE))
        : false,
      text: isPDFField
        ? String(fieldInDocument.getValue())
        : fieldInDocument.type === 'INPUT'
          ? fieldInDocument.text
          : '',
    },
  };
}

export type FieldLocation =
  | { __typename: Apollo.DocumentVersion['__typename']; at?: Range }
  | { __typename: Apollo.StaticFileVersion['__typename']; at?: PDFFieldLocation };
type NewField =
  | WithLocalFieldProps<
      Pick<Apollo.DocumentVariable, '__typename' | 'label' | 'nodeId' | 'value' | 'variant'>
    >
  | Pick<Apollo.SignatureField, '__typename' | 'nodeId'>;

interface InsertFieldArgs {
  isDynamic: boolean;
  locationInfo: FieldLocation;
  newField: NewField;
  checkboxOptions?: {
    choiceRefId: UUID;
    id: UUID;
    optionText?: string | null;
  }[];
  select?: boolean;
  selectedAnswerIds?: UUID[];
}

interface UseInsertFieldIntoDocumentOptions {
  documentViewer: Core.DocumentViewer | null;
  editingMode: EditingMode | null;
  editor: AlineClientEditor | null;
  tabId: UUID | null;
}

/**
 * Insertion mutates either Slate or Apryse state. The explicit handles make
 * runtime-boundary callsites, like global DnD, pass the active tab's runtime
 * instead of depending on ambient React context.
 */
export function useInsertFieldIntoDocument({
  documentViewer,
  editingMode,
  editor,
  tabId,
}: UseInsertFieldIntoDocumentOptions): {
  insertFieldIntoDocument: (args: InsertFieldArgs) => Promise<void>;
} {
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { document } = useGetCurrentDoc();
  const { variables } = useGetVariablesForDocument(document?.id ?? '', { skip: !document?.id });
  const { createPDFCheckboxField, createPDFNonSigField, createPDFSignatureField } = usePDFFieldAPI(
    documentViewer,
    tabId
  );
  const insertFieldIntoDocument = useCallback(
    async (args: InsertFieldArgs): Promise<void> => {
      const {
        checkboxOptions,
        isDynamic,
        locationInfo,
        newField,
        select = true,
        selectedAnswerIds,
      } = args;
      const isCheckboxVar =
        newField.__typename === 'DocumentVariable' &&
        newField.variant === Apollo.MetadataVariant.Checkbox;
      const variable = variables?.find(v => v.nodeId === newField.nodeId);
      const resolvedCheckboxOptions = isCheckboxVar
        ? checkboxOptions?.length
          ? checkboxOptions
          : (variable?.answerOptions?.options?.map(o => ({
              choiceRefId: o.choiceRefId,
              id: o.id,
              optionText: o.optionText,
            })) ?? [])
        : null;
      if (locationInfo.__typename === 'StaticFileVersion') {
        switch (newField.__typename) {
          case 'DocumentVariable': {
            if (isCheckboxVar) {
              if (!resolvedCheckboxOptions?.length) return;
              return createPDFCheckboxField({
                checkboxOptions: resolvedCheckboxOptions,
                location: locationInfo.at,
                nodeId: newField.nodeId,
                selectedAnswerIds,
              });
            }

            return createPDFNonSigField({
              location: locationInfo.at,
              multiLine: newField.multiLine,
              nodeId: newField.nodeId,
              value: newField.value,
              variant: newField.variant,
            });
          }
          case 'SignatureField': {
            return createPDFSignatureField({
              location: locationInfo.at,
              nodeId: newField.nodeId,
            });
          }
        }
      }

      if (!currentUserId || !editingMode) return;

      switch (newField.__typename) {
        case 'DocumentVariable': {
          if (!editor) return;

          if (isCheckboxVar) {
            if (!resolvedCheckboxOptions?.length) return;
            const optionIdentities = resolvedCheckboxOptions.map(option => ({
              ...option,
              optionText: option.optionText ?? '',
            }));
            return AuthorizedTransforms.insertCheckboxOptionFields(editor, {
              answerOptions: getCheckboxOptionIdentities(optionIdentities),
              at: locationInfo.at,
              authorId: currentUserId,
              editingMode,
              fieldId: newField.nodeId,
              select,
              selectedAnswerIds: toCheckboxChoiceRefIds(selectedAnswerIds ?? [], optionIdentities),
            });
          }

          const fieldVariant = variableVariantToFieldVariant(newField.variant);
          if (isDynamic)
            return AuthorizedTransforms.insertDynamicContent(editor, {
              at: locationInfo.at,
              authorId: currentUserId,
              dynamicContentProps: {
                fieldId: newField.nodeId,
                initialTriggerIndex: 'not-set',
                triggerIndex: 'not-set',
                triggers: [],
                type: 'dynamic-block',
                variant:
                  newField.variant === Apollo.MetadataVariant.Options
                    ? Apollo.FieldVariant.Dropdown
                    : Apollo.FieldVariant.Radiobutton,
              },
              editingMode,
              select,
            });

          return AuthorizedTransforms.insertInputField(editor, {
            at: locationInfo.at,
            authorId: currentUserId,
            editingMode,
            props: {
              fieldId: newField.nodeId,
              variant: fieldVariant as Exclude<typeof fieldVariant, Apollo.FieldVariant.Checkbox>,
            },
            select,
            text: newField.value || newField.label,
          });
        }
        case 'SignatureField': {
          if (!editor) return;

          return AuthorizedTransforms.insertSignatureField(editor, {
            at: locationInfo.at,
            authorId: currentUserId,
            editingMode,
            props: {
              fieldId: newField.nodeId,
              variant: Apollo.FieldVariant.Signature,
            },
            select,
          });
        }
      }
    },
    [
      currentUserId,
      editingMode,
      createPDFNonSigField,
      createPDFCheckboxField,
      createPDFSignatureField,
      AuthorizedTransforms,
      editor,
      variables,
    ]
  );

  return { insertFieldIntoDocument };
}

export function useFieldFromCache(nodeId: UUID | null) {
  const { documentId } = useDocumentParams();
  const { data, loading } = Apollo.useGetDbFieldsQuery({
    skip: !nodeId,
    variables: { documentId },
  });
  const field = useMemo(() => {
    if (!nodeId) return null;
    const variables = data?.document.variables ?? [];
    const signatureFields = data?.document.signatureFields ?? [];
    return [...variables, ...signatureFields].find(field => field.nodeId === nodeId) ?? null;
  }, [data?.document.signatureFields, data?.document.variables, nodeId]);

  return { field, loading: Boolean(nodeId) && loading };
}

export function useFieldsChanged(tabId: UUID | null) {
  const pdfFields = useGetPDFFields(tabId);
  const { cannedFieldIdsInChildren, fieldIdsInChildren } = useGetFieldsInJSON();
  const uniqPDFFieldNodeIds = uniq(pdfFields.map(field => field.name));
  const prevCannedFieldIdsInChildren = usePrevious(cannedFieldIdsInChildren);
  const prevFieldIdsInChildren = usePrevious(fieldIdsInChildren);
  const prevPDFFieldIdsInDoc = usePrevious(uniqPDFFieldNodeIds);
  const cannedFieldIdsInChildrenChanged = !equals(
    cannedFieldIdsInChildren,
    prevCannedFieldIdsInChildren
  );
  const fieldIdsInChildrenChanged = !equals(fieldIdsInChildren, prevFieldIdsInChildren);
  const pdfFieldIdsChanged = !equals(uniqPDFFieldNodeIds, prevPDFFieldIdsInDoc);

  return {
    fieldsChanged:
      cannedFieldIdsInChildrenChanged || fieldIdsInChildrenChanged || pdfFieldIdsChanged,
  };
}
